import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { rolesAPI } from '../../roles/services/roles';
import { departmentsAPI } from '../../departments/services/departments';
import { STANDARD_ROLES } from '../data/standardRoles';
import { isDuplicateRoleError } from '../utils/apiErrors';

export function useStandardRoles() {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState(null);
  // Keyed by "resource:action" → permission ({ permission_id, name, ... })
  const [permissions, setPermissions] = useState({});
  const permissionsRef = useRef(null);

  const loadPermissions = useCallback(async () => {
    if (permissionsRef.current) return permissionsRef.current;
    const res = await departmentsAPI.getMyDepartmentModulesWithPermissions();
    const byKey = {};
    (res.data?.modules || []).forEach(m =>
      (m.permissions || []).forEach(p => { byKey[`${p.resource}:${p.action}`] = p; })
    );
    permissionsRef.current = byKey;
    setPermissions(byKey);
    return byKey;
  }, []);

  // Returns the translated role names that were created, skipped (already exist) and failed
  const createStandardRoles = useCallback(async (selectedIds, existingRoleNames = []) => {
    const presets = STANDARD_ROLES.filter(r => selectedIds.includes(r.id));
    const existing = new Set(existingRoleNames.map(n => n.trim().toLowerCase()));
    const result = { created: [], skipped: [], failed: [] };

    setCreating(true);
    setProgress({ current: 0, total: presets.length, name: '' });
    try {
      let permMap;
      try {
        permMap = await loadPermissions();
      } catch {
        result.failed = presets.map(p => t(`onboarding:standard_roles.${p.id}.name`));
        return result;
      }

      for (const [index, preset] of presets.entries()) {
        const name = t(`onboarding:standard_roles.${preset.id}.name`);
        setProgress({ current: index + 1, total: presets.length, name });

        if (existing.has(name.trim().toLowerCase())) {
          result.skipped.push(name);
          continue;
        }

        // One failing role must not abort the remaining ones
        try {
          const roleRes = await rolesAPI.createRole({
            name,
            description: t(`onboarding:standard_roles.${preset.id}.description`),
          });
          const roleId = roleRes.data?.role_id;
          if (!roleId) throw new Error('Missing role_id in response');

          const permissionIds = preset.permissions
            .map(p => permMap[p]?.permission_id)
            .filter(Boolean);

          if (permissionIds.length > 0) {
            await rolesAPI.bulkAddPermissions(roleId, { permission_ids: permissionIds });
          }
          result.created.push(name);
        } catch (e) {
          (isDuplicateRoleError(e) ? result.skipped : result.failed).push(name);
        }
      }
      return result;
    } finally {
      setCreating(false);
      setProgress(null);
    }
  }, [loadPermissions, t]);

  return { createStandardRoles, loadPermissions, permissions, creating, progress };
}
